/**
 * Home: a slim hero with setup steps until the basics exist, what is running
 * now (live), your agents as cards, and the latest experiments. An annotator
 * sees none of those, so their Home is Annotate.
 */

import { sourceName } from "@/features/experiments/status";
import { Link, Navigate } from "react-router";
import { Box } from "@/components/ui/box";
import { ButtonLink } from "@/components/ui/button";
import { Progress } from "@/components/ui/page";
import { EmptyState, QueryState } from "@/components/ui/states";
import { useAgents } from "@/features/agents/hooks";
import { useExperiments } from "@/features/experiments/hooks";
import { isLive, runProgress } from "@/features/experiments/status";
import { ExperimentStatusPill } from "@/features/experiments/status-pill";
import type { ExperimentSummary } from "@/features/experiments/types";
import { useProject } from "@/features/projects/hooks";
import { ProjectAction } from "@/features/projects/project-action";
import { formatRelative, formatScore } from "@/lib/format";
import { AgentCards } from "./agent-cards";
import { HomeHero } from "./getting-started";
import { RecentExperiments } from "./recent-experiments";
import { datasetLabel } from "@/features/experiments/dataset-label";

function Feed({
  title,
  experiments,
  empty,
}: {
  title: string;
  experiments: ExperimentSummary[];
  empty: string;
}) {
  return (
    <Box label={title}>
      <div className="flex items-center justify-between px-4 py-3">
        <h2 className="text-sm font-semibold">{title}</h2>
        <span className="text-xs text-muted">{experiments.length}</span>
      </div>
      {experiments.length === 0 && (
        <p className="border-t border-line-soft px-4 py-3 text-xs text-muted">{empty}</p>
      )}
      {experiments.map((e) => (
        <Link
          key={e.id}
          to={`/experiments/${e.id}`}
          className="flex flex-col gap-1 border-t border-line-soft px-4 py-3 hover:bg-hover"
        >
          <span className="flex justify-between gap-2">
            <span className="truncate text-[13px] font-semibold">{sourceName(e)}</span>
            {e.aggregate?.overall != null ? (
              <span className="font-mono text-[13px]">{formatScore(e.aggregate.overall)}</span>
            ) : (
              <ExperimentStatusPill experiment={e} />
            )}
          </span>
          <span className="truncate text-xs text-muted">
            {e.name} · {e.dataset ? datasetLabel(e.dataset) : "Traces"} · {formatRelative(e.created_at)}
          </span>
          {isLive(e.status) && (
            <span className="flex items-center gap-2.5 pt-1.5">
              <span className="flex-1">
                <Progress {...runProgress(e)} label={`${e.name} progress`} />
              </span>
              <span className="font-mono text-[11px] text-muted">
                {runProgress(e).value} / {runProgress(e).max}
              </span>
            </span>
          )}
        </Link>
      ))}
    </Box>
  );
}

function SectionHead({ title, count, to }: { title: string; count?: number | undefined; to?: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <h2 className="text-[15px] font-semibold">
        {title} {count != null && <span className="font-mono text-xs font-normal text-muted">{count}</span>}
      </h2>
      {to && (
        <Link className="link text-xs font-medium" to={to}>
          View all
        </Link>
      )}
    </div>
  );
}

export function HomePage() {
  // Checked before HomeContent mounts, so none of its lists is ever asked for.
  if (!useProject().can("experiments:read")) return <Navigate to="/annotation" replace />;
  return <HomeContent />;
}

function HomeContent() {
  const agents = useAgents();
  const experiments = useExperiments();
  const all = experiments.data ?? [];
  const running = all.filter((e) => isLive(e.status));
  const recent = all.filter((e) => !isLive(e.status)).slice(0, 6);

  return (
    <div className="flex flex-col gap-8">
      <HomeHero hasExperiments={all.length > 0} />
      {running.length > 0 && <Feed title="Running now" experiments={running} empty="Nothing is running." />}
      <section className="flex flex-col gap-3" aria-label="Your agents">
        <SectionHead title="Agents" count={agents.data?.length} to="/agents" />
        <QueryState query={agents} what="agents">
          {agents.data?.length ? (
            <AgentCards agents={agents.data} />
          ) : (
            <Box>
              <EmptyState
                title="No agents yet"
                action={
                  <ProjectAction permission="experiments:write">
                    <ButtonLink variant="primary" to="/experiments/new">
                      New experiment
                    </ButtonLink>
                  </ProjectAction>
                }
              >
                An agent appears here when you save its first experiment: pick a connection, an agent and a
                version, a dataset and evaluators.
              </EmptyState>
            </Box>
          )}
        </QueryState>
      </section>
      <section className="flex flex-col gap-3" aria-label="Recent experiments">
        <SectionHead title="Recent experiments" to="/experiments" />
        <QueryState query={experiments} what="experiments">
          <Box>
            {recent.length ? (
              <RecentExperiments experiments={recent} />
            ) : (
              <p className="px-4 py-3 text-xs text-muted">No experiments yet.</p>
            )}
          </Box>
        </QueryState>
      </section>
    </div>
  );
}
